import * as Sentry from "@sentry/react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";

import HomeClient from "@/app/HomeClient";
import { useToast } from "@/components/ui/ToastProvider";
import { useAuth } from "@/hooks/useAuth";
import { resolveDefaultCampaignShortId } from "@/lib/lastOpenedCampaign";
import { useUserCampaignsQuery } from "@/lib/queries/campaignQueries";
import { hasPendingCloudOnboarding } from "@/state/pendingCloudOnboarding";
import { useCampaignsModalStore } from "@/state/useCampaignsModalStore";

export default function HomePage() {
  const { user, loading } = useAuth();
  const { onboarding, error: errorParam } = useSearch({ from: "/" });
  const navigate = useNavigate();
  const toast = useToast();
  const openCampaignsModal = useCampaignsModalStore((s) => s.openModal);
  const showedAuthErrorRef = useRef(false);
  const didRedirectRef = useRef(false);
  const didOpenEmptyModalRef = useRef(false);

  useEffect(() => {
    if (errorParam && !showedAuthErrorRef.current) {
      showedAuthErrorRef.current = true;
      toast.error(errorParam);
    }
  }, [errorParam, toast]);

  // Treat a pending cloud-onboarding payload as the same "finalize" mode that
  // `?onboarding=1` triggers. This covers the in-tab signup flow where the
  // user signs up from the local-only editor and never round-trips through a
  // URL that carries the query param.
  const finalizeOnboarding = onboarding === "1" || (Boolean(user) && hasPendingCloudOnboarding());

  const shouldFetchCampaigns = Boolean(user) && !loading && !finalizeOnboarding;
  const campaignsQuery = useUserCampaignsQuery(undefined, shouldFetchCampaigns);
  const campaignsReady = !shouldFetchCampaigns || campaignsQuery.isFetched;
  const campaigns = campaignsQuery.data ?? null;

  const resolvedShortId = useMemo(() => {
    if (!user || !campaignsReady) return null;
    return resolveDefaultCampaignShortId(campaigns, user.id);
  }, [campaigns, campaignsReady, user]);

  const noCampaignsForSignedInUser =
    Boolean(user) && !loading && !finalizeOnboarding && campaignsReady && resolvedShortId === null;

  useEffect(() => {
    if (loading || !user || finalizeOnboarding || didRedirectRef.current) {
      return;
    }
    if (!resolvedShortId) {
      return;
    }
    didRedirectRef.current = true;
    navigate({
      to: "/campaign/$shortId",
      params: { shortId: resolvedShortId },
      replace: true,
    });
  }, [loading, user, finalizeOnboarding, resolvedShortId, navigate]);

  useEffect(() => {
    if (!noCampaignsForSignedInUser || didOpenEmptyModalRef.current) {
      return;
    }
    didOpenEmptyModalRef.current = true;
    Sentry.logger.info("home_page.no_campaigns_for_signed_in_user", { userId: user?.id ?? "(unknown)" });
    openCampaignsModal(null);
  }, [noCampaignsForSignedInUser, openCampaignsModal, user?.id]);

  const entry = useMemo(
    () => ({ kind: "local-root" as const, finalizeOnboarding }),
    [finalizeOnboarding],
  );

  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-[#fffaf1]">
        <p className="text-lg text-neutral-700">Loading...</p>
      </div>
    );
  }

  if (user && !finalizeOnboarding && !noCampaignsForSignedInUser) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-[#fffaf1]">
        <p className="text-lg text-neutral-700">Loading...</p>
      </div>
    );
  }

  return <HomeClient entry={entry} />;
}
